import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Flame, TrendingUp, TrendingDown, Medal, Search, Calendar,
  Sparkles, Target,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const SRC_ROUTE = {
  deal_stage: (id) => `/deals/${id}`,
  deal_won: (id) => `/deals/${id}`,
  deal_lost: (id) => `/deals/${id}`,
  deal: (id) => `/deals/${id}`,
  followup: (id) => `/followups`,
  meeting_completed: () => `/calendar`,
  meeting: () => `/calendar`,
  quotation: (id) => `/quotations`,
  client: (id) => `/clients/${id}`,
  contact: () => `/contacts`,
};

function sourceLink(ev) {
  const fn = SRC_ROUTE[ev.source_type];
  if (!fn || !ev.source_id) return null;
  return fn(ev.source_id);
}

function pointsTone(n) {
  if (n > 0) return "text-emerald-700";
  if (n < 0) return "text-red-700";
  return "text-muted-foreground";
}

export default function PerformanceUserDetail() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/performance/admin/users/${uid}`, { params: { limit: 500 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { toast.error(fmtError(err)); navigate("/performance"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [uid, navigate]);

  const filteredEvents = useMemo(() => {
    if (!data?.recent_events) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.recent_events;
    return data.recent_events.filter((ev) =>
      (ev.label || "").toLowerCase().includes(needle)
      || (ev.activity_key || "").toLowerCase().includes(needle)
      || (ev.source_type || "").toLowerCase().includes(needle),
    );
  }, [data, q]);

  if (loading) {
    return (
      <div className="p-6 sm:p-8 space-y-4 max-w-[1100px] mx-auto">
        <Skeleton className="h-10 w-48 rounded-2xl" />
        <Skeleton className="h-32 rounded-3xl" />
        <Skeleton className="h-64 rounded-3xl" />
      </div>
    );
  }
  if (!data) return null;

  const { user, totals, streaks, qualification, milestones, summary, recent_events } = data;

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto" data-testid="performance-user-detail">
      <Link
        to="/performance"
        className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground mb-4 tap"
        data-testid="back-performance"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Performance Hub
      </Link>

      {/* Hero — name + role + headline points */}
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">
            {user.role || "Team member"}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1 break-words">
            {user.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Every point this teammate has earned, grouped by action and listed chronologically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`text-sm font-extrabold px-3 py-1.5 rounded-xl ${
              qualification?.qualified
                ? "bg-primary-50 text-primary-700 border-primary-200"
                : "bg-muted/60 text-muted-foreground border-border/60"
            }`}
            data-testid="perf-user-qualification"
          >
            <Target className="h-3.5 w-3.5 mr-1" />
            {qualification?.qualified ? "Qualified this month" : `${qualification?.gaps?.length || 0} gap(s)`}
          </Badge>
        </div>
      </header>

      {/* Totals card */}
      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile
              label="Total points"
              value={totals.total_points}
              icon={<Sparkles className="h-4 w-4" />}
              tone="primary"
              testId="perf-total-points"
            />
            <StatTile
              label="Activity points"
              value={totals.activity_points}
              icon={<TrendingUp className="h-4 w-4" />}
              testId="perf-activity-points"
            />
            <StatTile
              label="Bonus points"
              value={totals.bonus_points}
              icon={<Medal className="h-4 w-4" />}
              tone="amber"
              testId="perf-bonus-points"
            />
            <StatTile
              label="Milestones"
              value={milestones?.length || 0}
              icon={<Medal className="h-4 w-4" />}
              testId="perf-milestones-count"
            />
          </div>

          {streaks?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-border/40 flex flex-wrap gap-2">
              {streaks.map((s) => (
                <span
                  key={s.type}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/50 text-xs font-semibold"
                  data-testid={`perf-streak-${s.type}`}
                >
                  <Flame className="h-3.5 w-3.5 text-amber-600" />
                  {s.type === "daily_followup" ? "Daily" : s.type === "weekly_activity" ? "Weekly" : "Monthly"}: {s.count}
                  {s.level ? <span className="opacity-70">· {s.level}</span> : null}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown — grouped by action */}
      <section className="mb-6">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-display text-xl font-extrabold tracking-tight">Why these points?</h2>
            <p className="text-xs text-muted-foreground">Grouped by action — total contribution, count, last earned.</p>
          </div>
        </div>
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-2 sm:p-3 space-y-2">
            {(!summary || summary.length === 0) ? (
              <p className="py-10 text-center text-sm text-muted-foreground">No point events yet.</p>
            ) : summary.map((row) => (
              <div
                key={row.activity_key}
                className="p-4 rounded-2xl border border-border/60 flex items-center gap-3"
                data-testid={`perf-summary-row-${row.activity_key}`}
              >
                <div className={`h-10 w-10 rounded-2xl grid place-items-center shrink-0 ${
                  row.total_points >= 0 ? "bg-primary-50 text-primary-700" : "bg-red-50 text-red-600"
                }`}>
                  {row.total_points >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-snug truncate">{row.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {row.count}×
                    {/* Show the distinct entity count next to the event count
                        so "43× stage moves" doesn't read like the same deal
                        was moved 43 times. 43 events across 8 deals is
                        legitimate; 43 events on 1 deal would be suspicious. */}
                    {typeof row.unique_sources === "number" && row.unique_sources > 0 && row.unique_sources < row.count && (
                      <span className="opacity-80"> · across {row.unique_sources} {row.activity_key === "stage_moved" ? "deal" : "record"}{row.unique_sources === 1 ? "" : "s"}</span>
                    )}
                    {" · last on "}{fmtDateTime(row.latest_at)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`font-display text-lg font-extrabold ${pointsTone(row.total_points)}`}>
                    {row.total_points > 0 ? "+" : ""}{row.total_points}
                  </p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">pts</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {/* Recent events — searchable chronological list */}
      <section>
        <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-extrabold tracking-tight">Event log</h2>
            <p className="text-xs text-muted-foreground">Latest {recent_events?.length || 0} point events.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search actions…"
              className="pl-9 h-10 rounded-2xl bg-muted/40"
              data-testid="perf-events-search"
            />
          </div>
        </div>

        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-0 divide-y divide-border/40">
            {filteredEvents.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">No events match.</p>
            ) : filteredEvents.map((ev) => {
              const link = sourceLink(ev);
              const Row = (
                <div className="p-4 flex items-center gap-3 hover:bg-muted/30 tap transition">
                  <div className="h-9 w-9 rounded-2xl bg-muted/50 grid place-items-center shrink-0">
                    {ev.is_bonus ? <Medal className="h-4 w-4 text-amber-600" /> : <Calendar className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-snug line-clamp-1">{ev.label || ev.activity_key}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {fmtDateTime(ev.created_at)}
                      {ev.source_type ? <span> · {ev.source_type.replace("_", " ")}</span> : null}
                      {ev.metadata?.value ? <span> · ₹{Number(ev.metadata.value).toLocaleString("en-IN")}</span> : null}
                    </p>
                  </div>
                  <div className={`font-display font-extrabold text-base shrink-0 ${pointsTone(ev.points_awarded || 0)}`}>
                    {ev.points_awarded > 0 ? "+" : ""}{ev.points_awarded}
                  </div>
                </div>
              );
              return link ? (
                <Link
                  key={ev.id || `${ev.activity_key}-${ev.created_at}-${ev.source_id}`}
                  to={link}
                  data-testid={`perf-event-${ev.activity_key}-${ev.source_id || "na"}`}
                  className="block"
                >
                  {Row}
                </Link>
              ) : (
                <div
                  key={ev.id || `${ev.activity_key}-${ev.created_at}-${ev.source_id}`}
                  data-testid={`perf-event-${ev.activity_key}-${ev.source_id || "na"}`}
                >
                  {Row}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function StatTile({ label, value, icon, tone, testId }) {
  const toneClass = tone === "primary"
    ? "bg-primary-50 text-primary-700"
    : tone === "amber"
    ? "bg-amber-50 text-amber-700"
    : "bg-muted/50 text-muted-foreground";
  return (
    <div className="p-4 rounded-2xl bg-card border border-border/60" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className={`h-7 w-7 rounded-xl grid place-items-center ${toneClass}`}>{icon}</span>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{label}</p>
      </div>
      <p className="font-display text-2xl font-extrabold mt-2 tabular-nums">{value ?? 0}</p>
    </div>
  );
}
